// ════════════════════════════════════════════════════════════════
// FloorCost — Sign-in surface
// ────────────────────────────────────────────────────────────────
// Mounted at `/` when the operator isn't signed in. Designed hero
// composition over a subtle gradient backdrop, single email input,
// big primary action, "Check your email" state with a resend
// cooldown. PR-1b layers passkey-primary sign-in on top: if a
// passkey is registered on this device, a "Sign in with passkey"
// button surfaces above the email form for a single-prompt return.
//
// Lifecycle: this module exports a single `renderSignIn(root)` fn
// that idempotently renders into the given container. The router
// in src/boot.js calls it. Component owns its own state via a
// closure — no global signals needed for a one-shot surface.
// ════════════════════════════════════════════════════════════════

import { html, render } from 'lit';
import {
  clearAuthCallbackError,
  getSignOutNotice,
  describeSignOutNotice,
  getAuthCallbackError,
  signInWithMagicLink,
  verifyOtp,
} from './index.js';
import { hasPasskey, isPasskeySupported, signInWithPasskey } from './passkey.js';

const RESEND_COOLDOWN_MS = 30_000;

// One live instance per root. The boot router re-invokes renderSignIn
// on every auth-state change and navigation, and each invocation is a
// fresh closure — without this claim registry a superseded instance's
// cooldown repaint timer keeps firing and paints its stale view over
// the new instance's. The newest render claims the root; older
// closures see the claim change and go inert.
/** @type {WeakMap<HTMLElement, { timer: ReturnType<typeof setTimeout> | null, host: HTMLElement }>} */
const _instances = new WeakMap();

/**
 * Render the sign-in surface into `root`. Idempotent — safe to
 * re-invoke whenever the route or session state changes; the previous
 * invocation's pending repaint timer is cancelled and its closure
 * stops painting.
 *
 * @param {HTMLElement} root
 */
export function renderSignIn(root) {
  const prev = _instances.get(root);
  if (prev && prev.timer !== null) clearTimeout(prev.timer);
  // Fresh host element per instance: lit caches its render part on the
  // container it renders into, and the boot router clears #boot-root
  // with innerHTML when handing over to the app — ejecting the cached
  // part's marker nodes. Rendering into the root itself after that
  // leaves the surface permanently blank (or throws in lit dev mode).
  // A per-instance host owns its part for its whole life, so the
  // surface always renders no matter what happened to the root in
  // between sign-ins.
  const host = document.createElement('div');
  /** @type {{ timer: ReturnType<typeof setTimeout> | null, host: HTMLElement }} */
  const inst = { timer: null, host };
  _instances.set(root, inst);
  root.replaceChildren(host);

  /** @type {'idle' | 'sending' | 'sent' | 'error' | 'passkey'} */
  let phase = 'idle';
  let email = '';
  let lastSentAt = 0;
  let errorMsg = '';
  // A failed auth callback (e.g. an expired magic link) is reported by
  // GoTrue as a URL error fragment; consumeCallbackHash() stashes the
  // message so the operator lands here knowing the click failed and
  // that requesting a fresh link is the way out.
  const callbackError = getAuthCallbackError();
  if (callbackError) {
    phase = 'error';
    errorMsg = callbackError;
  }
  // AUTH-2: the result of the sign-out that landed the operator here, when it
  // did NOT revoke server-side (passkey registered, or the server was
  // unreachable). Kept out of `errorMsg`/`phase` deliberately — the sign-out
  // succeeded locally, so styling it as a sign-in failure would misdescribe it.
  // Read, not consumed: the boot router builds several closures per paint
  // chain, so a consume-on-read is swallowed by one whose output is discarded.
  // getSignOutNotice() is one-shot per page load, not per call.
  const signOutNotice = describeSignOutNotice(getSignOutNotice());
  // OTP-code path (works inside an iOS PWA where the magic link
  // would open in Safari and never reach the standalone app).
  let otp = '';
  let otpError = '';
  let verifying = false;
  // Snapshot at first paint so the affordance doesn't appear /
  // disappear mid-session as passkeys are added/removed elsewhere.
  const offerPasskey = isPasskeySupported() && hasPasskey();

  const cooldownLeft = () => {
    const left = lastSentAt + RESEND_COOLDOWN_MS - Date.now();
    return Math.max(0, Math.ceil(left / 1000));
  };

  function paint() {
    // Superseded by a later renderSignIn call (new claim) or unmounted
    // by the app route (host detached) — this closure is stale: don't
    // render, don't re-arm.
    if (_instances.get(root) !== inst || host.parentNode !== root) return;
    render(view(), host);
    // Schedule a re-paint while the resend cooldown is ticking down
    // so the button label refreshes. Skipped at phase !== 'sent'.
    // Single handle: a paint mid-cooldown (e.g. an OTP keystroke)
    // replaces the pending tick instead of spawning a parallel chain.
    if (inst.timer !== null) {
      clearTimeout(inst.timer);
      inst.timer = null;
    }
    if (phase === 'sent' && cooldownLeft() > 0) {
      inst.timer = setTimeout(paint, 1000);
    }
  }

  async function submit(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (phase === 'sending') return;
    if (phase === 'sent' && cooldownLeft() > 0) return;
    const trimmed = email.trim();
    if (!trimmed || !/^.+@.+\..+$/.test(trimmed)) {
      phase = 'error';
      errorMsg = 'Please enter a valid email address';
      paint();
      return;
    }
    phase = 'sending';
    errorMsg = '';
    paint();
    try {
      await signInWithMagicLink(trimmed);
      phase = 'sent';
      lastSentAt = Date.now();
    } catch (err) {
      phase = 'error';
      errorMsg = err && err.message ? err.message : 'Sign-in request failed';
    }
    paint();
  }

  function onInput(e) {
    email = /** @type {HTMLInputElement} */ (e.target).value;
    if (phase === 'error') {
      phase = 'idle';
      errorMsg = '';
      // Typing is a dismissal — clear the module-level stash too, or
      // the next boot re-paint would re-seed a fresh closure with the
      // already-dismissed message.
      clearAuthCallbackError();
    }
    // Always repaint so the submit button's `?disabled` attribute
    // tracks the empty/non-empty state of the input. Without this,
    // the button stays frozen at first-paint's disabled state.
    paint();
  }

  function backToForm() {
    phase = 'idle';
    otp = '';
    otpError = '';
    verifying = false;
    paint();
  }

  async function passkeySignIn() {
    if (phase === 'passkey') return;
    phase = 'passkey';
    errorMsg = '';
    paint();
    try {
      await signInWithPasskey();
      // Success — auth state change wakes the boot router which
      // routes us into /app. No further paint needed.
    } catch (err) {
      phase = 'error';
      errorMsg = err && err.message
        ? err.message
        : 'Passkey sign-in failed';
      paint();
    }
  }

  function onOtpInput(e) {
    // Strip everything but digits so a pasted "123 456" or
    // "code: 123456" still verifies; cap at 6.
    otp = /** @type {HTMLInputElement} */ (e.target).value
      .replace(/\D/g, '')
      .slice(0, 6);
    if (otpError) otpError = '';
    paint();
  }

  async function verifyCode(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (verifying) return;
    if (otp.length < 6) {
      otpError = 'Enter the 6-digit code from the email';
      paint();
      return;
    }
    verifying = true;
    otpError = '';
    paint();
    try {
      await verifyOtp(email, otp);
      // Success — setSession fires onAuthStateChange, which wakes
      // the boot router and routes into /app. No further paint.
    } catch (err) {
      verifying = false;
      otpError = err && err.message
        ? 'That code didn’t work — check it and try again, or resend.'
        : 'Verification failed';
      paint();
    }
  }

  function view() {
    return html`
      <main class="signin-shell" role="main">
        <div class="signin-bg" aria-hidden="true"></div>
        <div class="signin-card">
          <header class="signin-brand">
            <img class="signin-logo" src="/logo-mark.svg" alt="" width="56" height="56">
            <div class="signin-wordmark">Floor<span class="signin-wordmark-accent">Cost</span></div>
            <p class="signin-tagline">Retail flooring costing &amp; sales tracking</p>
          </header>

          ${signOutNotice
            ? html`<p class="signin-notice" role="status">${signOutNotice}</p>`
            : null}

          ${phase === 'sent' ? sentView() : formView()}

          <footer class="signin-foot">
            <span>FloorCost</span>
            <span aria-hidden="true">·</span>
            <span>Brisbane QLD</span>
          </footer>
        </div>
      </main>
    `;
  }

  function passkeyAffordance() {
    if (!offerPasskey) return null;
    const busy = phase === 'passkey';
    return html`
      <div class="signin-passkey-row">
        <button
          class="signin-passkey"
          type="button"
          @click=${passkeySignIn}
          ?disabled=${busy || phase === 'sending'}
          aria-label="Sign in with passkey">
          ${busy
            ? html`<span class="signin-spinner signin-spinner--dark" aria-hidden="true"></span>Verifying…`
            : html`
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <rect x="3" y="11" width="18" height="10" rx="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  <circle cx="12" cy="16" r="1.5"/>
                </svg>
                Sign in with passkey
              `}
        </button>
        <div class="signin-divider" role="separator" aria-label="or">
          <span>or use email</span>
        </div>
      </div>
    `;
  }

  function formView() {
    const busy = phase === 'sending';
    return html`
      ${passkeyAffordance()}
      <form class="signin-form" @submit=${submit} novalidate>
        <label class="signin-label" for="signin-email">Email</label>
        <input
          id="signin-email"
          class="signin-input"
          type="email"
          inputmode="email"
          autocomplete="email"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
          required
          .value=${email}
          @input=${onInput}
          placeholder="you@example.com"
          ?disabled=${busy}
          enterkeyhint="send">
        ${phase === 'error'
          ? html`<p class="signin-error" role="alert">${errorMsg}</p>`
          : html`<p class="signin-hint">We'll email you a sign-in link — no password required.</p>`}
        <button class="signin-submit" type="submit" ?disabled=${busy || !email.trim()}>
          ${busy ? html`<span class="signin-spinner" aria-hidden="true"></span>Sending…` : 'Email me a sign-in link'}
        </button>
      </form>
    `;
  }

  function sentView() {
    const left = cooldownLeft();
    return html`
      <div class="signin-sent" role="status" aria-live="polite">
        <div class="signin-sent-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z"/>
            <path d="m4 8 8 6 8-6"/>
          </svg>
        </div>
        <h2 class="signin-sent-title">Check your email</h2>
        <p class="signin-sent-body">We sent a sign-in link to <strong>${email}</strong>. Click it to come back here, signed in.</p>

        <form class="signin-otp" @submit=${verifyCode}>
          <label class="signin-otp-label" for="signin-otp">On this device? Enter the 6-digit code instead</label>
          <div class="signin-otp-row">
            <input
              id="signin-otp"
              class="signin-otp-input"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              pattern="[0-9]*"
              maxlength="6"
              placeholder="000000"
              aria-label="6-digit sign-in code"
              .value=${otp}
              @input=${onOtpInput}
              ?disabled=${verifying}
              enterkeyhint="go">
            <button class="signin-otp-verify" type="submit" ?disabled=${verifying || otp.length < 6}>
              ${verifying ? html`<span class="signin-spinner" aria-hidden="true"></span>Verifying…` : 'Verify'}
            </button>
          </div>
          ${otpError
            ? html`<p class="signin-otp-error" role="alert">${otpError}</p>`
            : html`<p class="signin-otp-hint">Use this if the link opens a browser instead of the app (e.g. on iPad).</p>`}
        </form>

        <button class="signin-resend" type="button" @click=${submit} ?disabled=${left > 0}>
          ${left > 0 ? `Resend in ${left}s` : 'Resend link'}
        </button>
        <button class="signin-back" type="button" @click=${backToForm}>Use a different email</button>
      </div>
    `;
  }

  paint();
  // Autofocus the email input on first paint so the operator can
  // start typing immediately. Skipped on `sent` state. The 'error'
  // case is a first paint seeded from a failed callback — the form
  // is showing and the operator's next move is typing their email.
  if (phase === 'idle' || phase === 'error') {
    requestAnimationFrame(() => {
      const el = root.querySelector('#signin-email');
      if (el instanceof HTMLInputElement) el.focus();
    });
  }
}
